import { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const PROPERTY_TYPES = [
  { value: 'residential_uc', label: 'Residential Under Construction' },
  { value: 'residential_rtm', label: 'Residential Ready to Move' },
  { value: 'commercial_uc', label: 'Commercial Under Construction' },
  { value: 'commercial_rtm', label: 'Commercial Ready to Move' },
  { value: 'land', label: 'Land / Plot' },
  { value: 'mixed', label: 'Mixed Use' },
];

const GEO_AREAS = ['Delhi NCR', 'Mumbai Metropolitan Region', 'Bangalore', 'Hyderabad', 'Pune', 'Chennai', 'Kolkata', 'Other'];

export default function AdminProjectForm({ onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'residential_uc',
    sector: '',
    city: '',
    state: '',
    geographicalArea: 'Delhi NCR',
    developerName: '',
    developerLogoUrl: '',
    developerBio: '',
    reraNumber: '',
    reraCertificateUrl: '',
    targetAppreciation: '',
    description: '',
    googleMapsEmbed: '',
    brokerageTotal: 5.0,
    brokerageBrokerMax: 1.5,
    brokerageCashbackMax: 1.5
  });
  const [loading, setLoading] = useState(false);

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        name: formData.name,
        type: formData.type,
        sector: formData.sector,
        city: formData.city,
        state: formData.state,
        geographicalArea: formData.geographicalArea,
        developerName: formData.developerName,
        developerLogoUrl: formData.developerLogoUrl || null,
        developerBio: formData.developerBio || null,
        reraNumber: formData.reraNumber,
        reraCertificateUrl: formData.reraCertificateUrl || null,
        targetAppreciation: formData.targetAppreciation,
        description: formData.description,
        googleMapsEmbed: formData.googleMapsEmbed || null,
        brokerage: {
          total: formData.brokerageTotal,
          brokerShareMax: formData.brokerageBrokerMax,
          cashbackMax: formData.brokerageCashbackMax
        }
      };
      
      const response = await api.post('/admin/projects', payload);
      toast.success('Project created successfully!');
      if (onSuccess) onSuccess(response.data.projectId);
    } catch (error) {
      console.error('Project creation error:', error.response?.data || error);
      toast.error(error.response?.data?.detail?.[0]?.msg || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white/5 border border-white/10 px-4 py-3 text-white text-sm focus:border-white/30 focus:outline-none transition-colors";
  const labelCls = "text-xs text-gray-500 uppercase tracking-wider mb-2 block";

  return (
    <div className="p-10" data-testid="project-creation-form">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white mb-6 flex items-center gap-2" data-testid="back-to-projects">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <h1 className="text-2xl font-light text-white mb-2">Create New Project</h1>
      <p className="text-sm text-gray-500 mb-8">Each project represents a real estate development. Units (each with their own LLP) are added after project creation.</p>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="border border-white/10 p-8 mb-8" data-testid="section-project-details">
          <h2 className="text-lg text-white mb-6">Project Details</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelCls}>Project Name *</label>
              <input required value={formData.name} onChange={e => update('name', e.target.value)} className={inputCls} placeholder="e.g., Trump Towers" data-testid="input-project-name" />
            </div>
            <div>
              <label className={labelCls}>Property Type *</label>
              <select required value={formData.type} onChange={e => update('type', e.target.value)} className={inputCls} data-testid="select-project-type">
                {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Builder / Developer *</label>
              <input required value={formData.developerName} onChange={e => update('developerName', e.target.value)} className={inputCls} placeholder="e.g., Tribeca Developers" data-testid="input-developer-name" />
            </div>
            <div>
              <label className={labelCls}>Developer Logo URL</label>
              <input value={formData.developerLogoUrl} onChange={e => update('developerLogoUrl', e.target.value)} className={inputCls} placeholder="https://example.com/logo.png" data-testid="input-developer-logo" />
              <p className="text-xs text-gray-600 mt-1">Displayed on investor property detail page</p>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Developer Description</label>
              <textarea value={formData.developerBio} onChange={e => update('developerBio', e.target.value)} className={`${inputCls} h-24 resize-none`} placeholder="Brief writeup about the developer — track record, years of experience, notable projects..." data-testid="input-developer-bio" />
              <p className="text-xs text-gray-600 mt-1">Shown to investors on the property detail page</p>
            </div>
            <div>
              <label className={labelCls}>Geographical Area *</label>
              <select required value={formData.geographicalArea} onChange={e => update('geographicalArea', e.target.value)} className={inputCls} data-testid="select-geo-area">
                {GEO_AREAS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Sector / Locality *</label>
              <input required value={formData.sector} onChange={e => update('sector', e.target.value)} className={inputCls} placeholder="e.g., Sector 69" data-testid="input-sector" />
            </div>
            <div>
              <label className={labelCls}>City *</label>
              <input required value={formData.city} onChange={e => update('city', e.target.value)} className={inputCls} placeholder="e.g., Gurgaon" data-testid="input-city" />
            </div>
            <div>
              <label className={labelCls}>State *</label>
              <input required value={formData.state} onChange={e => update('state', e.target.value)} className={inputCls} placeholder="e.g., Haryana" data-testid="input-state" />
            </div>
            <div>
              <label className={labelCls}>RERA Number *</label>
              <input required value={formData.reraNumber} onChange={e => update('reraNumber', e.target.value)} className={`${inputCls} font-mono`} placeholder="e.g., RC/REP/HARERA/GGM/2024/123" data-testid="input-rera" />
            </div>
            <div>
              <label className={labelCls}>RERA Certificate URL</label>
              <input value={formData.reraCertificateUrl} onChange={e => update('reraCertificateUrl', e.target.value)} className={inputCls} placeholder="https://haryanarera.gov.in/..." data-testid="input-rera-cert-url" />
              <p className="text-xs text-gray-600 mt-1">Link to the RERA certificate — opens in new tab for investors</p>
            </div>
            <div>
              <label className={labelCls}>Target Appreciation</label>
              <input value={formData.targetAppreciation} onChange={e => update('targetAppreciation', e.target.value)} className={inputCls} placeholder="e.g., 5% annualized increase in Book Value" data-testid="input-target-appreciation" />
              <p className="text-xs text-gray-600 mt-1">Projected return benchmark for investor communications</p>
            </div>
            <div>
              <label className={labelCls}>Project Description</label>
              <input value={formData.description} onChange={e => update('description', e.target.value)} className={inputCls} placeholder="Optional — marketing description" data-testid="input-description" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Google Maps Embed URL</label>
              <input value={formData.googleMapsEmbed} onChange={e => update('googleMapsEmbed', e.target.value)} className={inputCls} placeholder='https://www.google.com/maps/embed?pb=...' data-testid="input-google-maps" />
              <p className="text-xs text-gray-600 mt-1">Paste Google Maps embed URL. Go to Google Maps → Share → Embed a map → copy the src URL from the iframe</p>
            </div>
          </div>
        </div>

        <div className="border border-white/10 p-8 mb-8">
          <h2 className="text-lg text-white mb-6">Brokerage Configuration</h2>
          <p className="text-sm text-gray-500 mb-6">Platform receives {formData.brokerageTotal}% from developer. Allocate to brokers and investor cashback.</p>
          
          <div className="space-y-8">
            <div>
              <div className="flex justify-between mb-3">
                <label className="text-xs text-gray-600 uppercase tracking-wider">Total Platform Brokerage</label>
                <span className="font-mono text-white">{formData.brokerageTotal}%</span>
              </div>
              <input type="range" min="0" max="10" step="0.1" value={formData.brokerageTotal} onChange={(e) => setFormData({...formData, brokerageTotal: parseFloat(e.target.value)})} className="w-full h-px bg-white/10 slider-thumb" />
              <div className="flex justify-between text-xs text-gray-600 mt-2"><span>0%</span><span>10%</span></div>
            </div>

            <div>
              <div className="flex justify-between mb-3">
                <label className="text-xs text-gray-600 uppercase tracking-wider">Max Broker Share (of capital raised)</label>
                <span className="font-mono text-white">{formData.brokerageBrokerMax}%</span>
              </div>
              <input type="range" min="0" max={formData.brokerageTotal} step="0.01" value={formData.brokerageBrokerMax} onChange={(e) => setFormData({...formData, brokerageBrokerMax: parseFloat(e.target.value)})} className="w-full h-px bg-white/10 slider-thumb" />
              <p className="text-xs text-gray-600 mt-2">Actual % based on broker tier (2.92% - 5.83% of this max)</p>
            </div>

            <div>
              <div className="flex justify-between mb-3">
                <label className="text-xs text-gray-600 uppercase tracking-wider">Max Investor Cashback (of capital raised)</label>
                <span className="font-mono text-white">{formData.brokerageCashbackMax}%</span>
              </div>
              <input type="range" min="0" max={formData.brokerageTotal} step="0.01" value={formData.brokerageCashbackMax} onChange={(e) => setFormData({...formData, brokerageCashbackMax: parseFloat(e.target.value)})} className="w-full h-px bg-white/10 slider-thumb" />
              <p className="text-xs text-gray-600 mt-2">Actual % based on investor tier (0.5% - 3% of this max)</p>
            </div>

            <div className="bg-white/5 border border-white/10 p-4">
              <p className="text-xs text-gray-600 mb-2">Platform Margin</p>
              <p className="font-mono text-2xl text-white">{(formData.brokerageTotal - formData.brokerageBrokerMax - formData.brokerageCashbackMax).toFixed(2)}%</p>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <button type="button" onClick={onBack} className="border border-white/10 hover:border-white/20 px-8 py-3 text-white">Cancel</button>
          <button type="submit" disabled={loading} className="bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold px-8 py-3">
            {loading ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </form>
    </div>
  );
}
